import { useState } from 'react';

import { LADDER_RUNGS } from '../engine/board.ts';
import type { Move } from '../engine/modes.ts';
import { TIER_COOL, TIER_NICE } from '../engine/rules.ts';
import { routeProgress } from './SolutionPanel.tsx';

// ***************************************************************************************
// The ladder panel: what to find, and the route that clears the board.
//
// TWO LISTS, because they are two different pieces of information and the difference
// between them is the whole game:
//
//   WORDS TO FIND    the five words, by rung. What.
//   PLAY IN THIS ORDER   the generator's own route. When.
//
// The second is the one that matters. Playing a word collapses the grid under the others,
// so the words alone are not a solution - the order is. The stored route is the sequence
// the board was BUILT from, and playing it back empties the board exactly.
//
// THIS IS AN EXPERIMENT, and showing it is the point: does knowing the answer make the
// puzzle easy? Both lists are gated on the server by REVEAL_SOLUTION, so a config change
// and a redeploy hides them again. Without the solution this falls back to listing the
// lengths, which gives nothing away.
//
// A player is not held to any of it. Any word of the right length fills its rung, and
// there are other routes that clear the board. Deviate and the rest of the stored route
// stops describing the board you are looking at - which the panel says, rather than
// leaving a now-wrong list sitting there looking authoritative.
// ***************************************************************************************

const HIDE_KEY = 'wordsoup.ladder.hideSolution';

function wordClass(tier: number): string {
  if (tier === TIER_COOL) return 'ladder__word ladder__word--cool';
  if (tier === TIER_NICE) return 'ladder__word ladder__word--nice';

  return 'ladder__word';
}

function readHidden(): boolean {
  try {
    return localStorage.getItem(HIDE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeHidden(hidden: boolean): void {
  try {
    localStorage.setItem(HIDE_KEY, hidden ? 'true' : 'false');
  } catch {
    /* a blocked store just means the choice does not survive a reload */
  }
}

export function LadderPanel({
  moves,
  solution,
  liveLength,
  liveValid,
}: {
  moves: readonly Move[];
  /** The generator's words, IN PLAY ORDER, when the site is revealing them. */
  solution: readonly string[] | undefined;
  /** Length of the selection being made, so the rung it would fill can light up. */
  liveLength: number;
  liveValid: boolean;
}): React.ReactElement {
  const [hidden, setHidden] = useState(readHidden);

  const revealing = solution !== undefined && !hidden;

  const found = new Map<number, Move>();

  for (const move of moves) {
    if (!found.has(move.word.length)) found.set(move.word.length, move);
  }

  const expected = new Map<number, string>();

  if (revealing) {
    for (const word of solution) {
      if (!expected.has(word.length)) expected.set(word.length, word);
    }
  }

  const remaining = LADDER_RUNGS.length - found.size;

  // How far the player has followed the stored route. The moment they play something
  // else, the rest of it describes a board that no longer exists. Shared with the
  // solution panel rather than worked out twice - two answers to "are they still on the
  // route?" would eventually disagree.
  const onRoute = solution ? routeProgress(moves, solution) : 0;

  const strayed = solution !== undefined && onRoute < moves.length;

  return (
    <section className="ladder" aria-label="Words to find">
      <div className="ladder__head">
        <h2 className="ladder__title">Words to find</h2>
        <span className="ladder__count">
          {remaining === 0 ? 'all found' : `${remaining} to go`}
        </span>
      </div>

      <ol className="ladder__list">
        {LADDER_RUNGS.map((rung) => {
          const move = found.get(rung);
          const want = expected.get(rung);
          const live = !move && liveValid && liveLength === rung;
          const swapped = move && want && move.word !== want;

          return (
            <li
              key={rung}
              className={
                'ladder__rung' +
                (move ? ' ladder__rung--done' : '') +
                (live ? ' ladder__rung--live' : '')
              }
            >
              <span className="ladder__length">{rung}</span>

              <span className="ladder__words">
                {move ? (
                  <span className={wordClass(move.tier)}>{move.word}</span>
                ) : want ? (
                  <span className="ladder__want">{want}</span>
                ) : (
                  <span className="ladder__blank" aria-label={`${rung} letters, not found yet`}>
                    {'–'.repeat(rung)}
                  </span>
                )}

                {swapped ? <span className="ladder__instead">not {want}</span> : null}
              </span>

              <span className="ladder__mark" aria-hidden="true">
                {move ? '✓' : live ? '•' : ''}
              </span>
            </li>
          );
        })}
      </ol>

      {/* ***********************************************************************************
          THE ROUTE. The order is the actual puzzle, so this is the real spoiler - which is
          why it is the thing worth experimenting with.
          *********************************************************************************** */}
      {revealing ? (
        <>
          <div className="ladder__head ladder__head--second">
            <h2 className="ladder__title">Play in this order</h2>
            <span className="ladder__count">{strayed ? 'off route' : 'the route'}</span>
          </div>

          <ol className="ladder__list ladder__list--route">
            {solution.map((word, index) => {
              const played = index < onRoute;
              const next = !strayed && index === onRoute;

              return (
                <li
                  key={`${word}-${index}`}
                  className={
                    'ladder__rung ladder__rung--step' +
                    (played ? ' ladder__rung--done' : '') +
                    (next ? ' ladder__rung--live' : '')
                  }
                >
                  <span className="ladder__length">{index + 1}</span>

                  <span className="ladder__words">
                    <span className={played ? 'ladder__word' : 'ladder__want'}>{word}</span>
                  </span>

                  <span className="ladder__mark" aria-hidden="true">
                    {played ? '✓' : next ? '▸' : ''}
                  </span>
                </li>
              );
            })}
          </ol>

          <p className="ladder__note">
            {strayed
              ? 'You have played something the route did not plan for, so the steps below it may no longer be on the board. Undo to get back to it, or find your own way.'
              : 'Knowing the words is not the hard part. Play one and the rest fall somewhere else — the order is the puzzle.'}
          </p>
        </>
      ) : null}

      {solution ? (
        <button
          className="button button--ghost ladder__toggle"
          type="button"
          onClick={() => {
            const next = !hidden;

            setHidden(next);
            writeHidden(next);
          }}
        >
          {hidden ? 'Show the solution' : 'Hide the solution'}
        </button>
      ) : null}
    </section>
  );
}
